/**
 * Extension service for Salvage Yard Chrome extension management.
 *
 * This service handles:
 * - Extension connection detection
 * - Version checking
 * - Update status (optional vs required)
 *
 * Usage:
 *
 * ```tsx
 * import { useExtensionStatus } from '../services/extension';
 *
 * function MyComponent() {
 *   const {
 *     connectionStatus,
 *     version,
 *     updateStatus,
 *     updateInfo,
 *   } = useExtensionStatus();
 *
 *   if (connectionStatus === 'not_installed') {
 *     return <ExtensionInstallBanner />;
 *   }
 *
 *   if (updateStatus === 'required') {
 *     return <UpdateRequiredBanner downloadUrl={updateInfo?.downloadUrl} />;
 *   }
 *
 *   // ...
 * }
 * ```
 */

// Types
export type {
  DownloadErrorType,
  ExtensionConnectionStatus,
  ExtensionStatus,
  ReleaseChannel,
  ReleaseInfo,
  UpdateCheckResult,
  UpdateStatus,
} from "./types";

export { DOWNLOAD_ERROR_MESSAGES } from "./types";

// Download utilities
export {
  getDownloadErrorMessage,
  getDownloadErrorType,
  isUrlExpiredError,
  startDownload,
} from "./downloadUtils";

// Version utilities
export {
  compareVersions,
  isValidVersionString,
  isVersionEqual,
  isVersionGreaterThan,
  isVersionGreaterThanOrEqual,
  isVersionLessThan,
  parseVersion,
} from "./versionUtils";

// API functions
export { createNoReleaseInfo, fetchReleaseInfo } from "./api";

// Chrome utilities
export { getChromeApi } from "./chrome";

// React hooks
export { useExtensionStatus } from "./hooks";

// Web search port communication
export {
  useWebSearchExtensionBridge,
  type WebSearchBridgeStatus,
  type WebSearchBridgeStatusResult,
  type WebSearchNewsSubmitPayload,
  type WebSearchContext,
} from "./webSearchPort";

// Components
export {
  ExtensionRequiredModal,
  type ExtensionRequiredModalProps,
  type ExtensionModalMode,
} from "./ExtensionRequiredModal";
